import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { apiService } from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface Business {
  id: string;
  name: string;
  shortName: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  // Trial / promotion fields
  validUntil?: string; // ISO string (timestamptz) from API
  referralCode?: string | null;
  trialId?: string | null;
  guidedSetupShown?: boolean;
  roles?: Array<{
    role: string;
    isActive: boolean;
  }>;
}

interface BusinessContextType {
  currentBusiness: Business | null;
  userBusinesses: Business[];
  setCurrentBusiness: (business: Business) => void;
  loadUserBusinesses: () => Promise<void>;
  clearBusinessContext: () => void;
  isLoading: boolean;
  /** True once we've attempted to resolve businesses for the current user session. */
  isInitialized: boolean;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (context === undefined) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
};

interface BusinessProviderProps {
  children: ReactNode;
}

export const BusinessProvider: React.FC<BusinessProviderProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [currentBusiness, setCurrentBusiness] = useState<Business | null>(null);
  const [userBusinesses, setUserBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasSetInitialBusiness = useRef(false);
  const previousUserId = useRef<string | null>(null);

  const loadUserBusinesses = useCallback(async () => {
    // Check if we have a valid token before making the request
    const token = localStorage.getItem('authToken');
    if (!token) {
      // Mark initialization complete so screens can surface a proper error state.
      setCurrentBusiness(null);
      setUserBusinesses([]);
      hasSetInitialBusiness.current = true;
      setIsInitialized(true);
      return;
    }
    
    setIsLoading(true);
    try {
      const businesses = await apiService.getUserBusinesses();
      
      // Filter out businesses where the user's role is disabled
      const activeBusinesses = businesses.filter(business => {
        const userRoleInBusiness = user?.roles?.find(role => 
          role.businessId === business.id
        );
        
        // If no role found or business role is disabled, exclude this business
        if (!userRoleInBusiness || userRoleInBusiness.businessRoleActive === false) {
          return false;
        }
        
        return true;
      });
      
      setUserBusinesses(activeBusinesses);

      if (activeBusinesses.length === 0) {
        setCurrentBusiness(null);
        hasSetInitialBusiness.current = true;
      } else {
        // Keep current business if it's still in the list; otherwise set to first (or preferred)
        setCurrentBusiness(prev => {
          if (prev && activeBusinesses.some(b => b.id === prev.id)) return prev;
          let selected = activeBusinesses[0];
          if (activeBusinesses.length > 1) {
            const professionalBusiness = activeBusinesses.find(b =>
              b.roles?.some((role: { role: string; isActive: boolean }) =>
                role.role === 'PROFESSIONAL' && role.isActive
              )
            );
            if (professionalBusiness) selected = professionalBusiness;
          }
          return selected;
        });
        hasSetInitialBusiness.current = true;
      }
    } catch (error) {
      console.error('Error loading user businesses:', error);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [user?.id, authLoading]); // Use user?.id so updating user (e.g. preferredServiceAddress) doesn't retrigger and cause loops

  const clearBusinessContext = useCallback(() => {
    setCurrentBusiness(null);
    setUserBusinesses([]);
    hasSetInitialBusiness.current = false;
    previousUserId.current = null;
    setIsInitialized(false);
  }, []);

  useEffect(() => {
    // Reset business context when user changes or logs out
    if (user?.id !== previousUserId.current) {
      // Reset all business state
      setCurrentBusiness(null);
      setUserBusinesses([]);
      hasSetInitialBusiness.current = false;
      setIsInitialized(false);
      previousUserId.current = user?.id || null;
    }
    
    // Only load businesses when user is authenticated, not loading, and we have a valid token
    if (user && !authLoading && localStorage.getItem('authToken')) {
      loadUserBusinesses();
    }
  }, [user, authLoading, loadUserBusinesses]);

  // Cleanup effect when user logs out
  useEffect(() => {
    if (!user && previousUserId.current !== null) {
      setCurrentBusiness(null);
      setUserBusinesses([]);
      hasSetInitialBusiness.current = false;
      previousUserId.current = null;
      setIsInitialized(false);
    }
  }, [user]);

  const value = {
    currentBusiness,
    userBusinesses,
    setCurrentBusiness,
    loadUserBusinesses,
    clearBusinessContext,
    isLoading,
    isInitialized
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}; 